import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { PageHeader, Section, EmptyState } from "../../components/Common";
import { toast } from "sonner";
import { CalendarCheck, Save } from "lucide-react";
import { useDataSync } from "../../lib/realtime";

const STATUS_COLORS = {
  present: "bg-emerald-500",
  absent: "bg-rose-500",
  late: "bg-amber-500",
};

export default function TeacherAttendance() {
  const [batches, setBatches] = useState([]);
  const [students, setStudents] = useState([]);
  const [batchId, setBatchId] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [marks, setMarks] = useState({}); // {student_id: status}
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/teacher/batches");
        setBatches(data);
        if (data.length && !batchId) setBatchId(data[0].id);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      }
    })();
  }, []);

  const loadBatchData = async () => {
    if (!batchId) return;
    setLoading(true);
    try {
      const [sRes, hRes] = await Promise.all([
        api.get(`/teacher/batches/${batchId}/students`),
        api.get("/teacher/attendance", { params: { batch_id: batchId } }),
      ]);
      setStudents(sRes.data);
      setHistory(hRes.data);
      // Pre-fill marks from the record for the selected date if it exists, else default present
      const existing = (hRes.data || []).find((r) => r.date === date);
      const init = {};
      sRes.data.forEach((s) => {
        const rec = existing?.records?.find((r) => r.student_id === s.id);
        init[s.id] = rec?.status || "present";
      });
      setMarks(init);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadBatchData(); /* eslint-disable-next-line */ }, [batchId, date]);
  useDataSync("attendance", () => loadBatchData());

  const setStatus = (sid, st) => setMarks((m) => ({ ...m, [sid]: st }));

  const save = async () => {
    try {
      const records = students.map((s) => ({ student_id: s.id, status: marks[s.id] || "present" }));
      await api.post("/teacher/attendance", { batch_id: batchId, date, records });
      toast.success("Attendance saved");
      loadBatchData();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div>
      <PageHeader
        title="Attendance"
        subtitle="Mark today's attendance in seconds. Existing records for the selected date can be edited."
        actions={
          <button onClick={save} disabled={!batchId || students.length === 0 || loading} data-testid="save-attendance"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-50">
            <Save className="h-4 w-4" /> Save
          </button>
        }
      />

      {batches.length === 0 && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="teacher-no-batches">
          You have no batches assigned. Ask the admin to assign you to a batch.
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3 mb-6">
        <select value={batchId} onChange={(e) => setBatchId(e.target.value)} data-testid="att-batch"
          className="w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 outline-none bg-white">
          <option value="">Select batch</option>
          {batches.map((b) => <option key={b.id} value={b.id}>{b.name} · {b.subject}</option>)}
        </select>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="att-date"
          className="w-full px-3 py-2.5 rounded-lg border border-slate-200 focus:border-blue-500 outline-none bg-white" />
      </div>

      {batchId && students.length === 0 && !loading ? (
        <EmptyState title="No students in this batch" desc="Ask admin to enroll students." icon={CalendarCheck} />
      ) : students.length > 0 && (
        <Section title={`Students (${students.length})`}>
          <div className="divide-y divide-slate-100">
            {students.map((s) => (
              <div key={s.id} className="flex items-center justify-between py-3" data-testid={`att-student-${s.id}`}>
                <div>
                  <div className="font-semibold text-slate-900">{s.name}{s.roll_number && <span className="ml-2 text-xs text-slate-500">Roll {s.roll_number}</span>}</div>
                  <div className="text-xs text-slate-500">{s.email}</div>
                </div>
                <div className="flex gap-1.5">
                  {["present", "absent", "late"].map((st) => (
                    <button key={st} onClick={() => setStatus(s.id, st)}
                      data-testid={`att-${s.id}-${st}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${
                        marks[s.id] === st ? `${STATUS_COLORS[st]} text-white` : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                      }`}>
                      {st}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      <div className="mt-8">
        <h2 className="font-heading font-bold text-lg text-slate-900 mb-3">Recent records</h2>
        <div className="space-y-2">
          {history.slice(0, 10).map((h) => {
            const p = h.records.filter((r) => r.status === "present").length;
            const a = h.records.filter((r) => r.status === "absent").length;
            const l = h.records.filter((r) => r.status === "late").length;
            return (
              <button key={h.id} onClick={() => setDate(h.date)} data-testid={`att-history-${h.date}`}
                className="w-full bg-white border border-slate-200 rounded-lg p-3 flex items-center justify-between hover:border-blue-300 text-left">
                <span className="text-sm text-slate-700">{h.date}</span>
                <span className="text-xs text-slate-500">
                  <span className="text-emerald-600 font-semibold">{p}P</span> · <span className="text-rose-600 font-semibold">{a}A</span> · <span className="text-amber-600 font-semibold">{l}L</span>
                </span>
              </button>
            );
          })}
          {history.length === 0 && <p className="text-sm text-slate-500">No history yet.</p>}
        </div>
      </div>
    </div>
  );
}
